/**
 * `beforeinstallprompt` fires ONCE, EARLY — typically before React has hydrated. A listener
 * registered inside a component's `useEffect` is therefore registered too late on a cold page
 * load and misses the event entirely, which is the classic reason an "Install" button never
 * appears. The listener must be attached at MODULE-EVALUATION time, before React renders.
 */

// ── BeforeInstallPromptEvent is not in the DOM lib ──────────────────────────

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

// ── Module-level state ────────────────────────────────────────────────

let deferredEvent: BeforeInstallPromptEvent | null = null;
let listeners: Array<() => void> = [];

function notifyListeners(): void {
  for (const listener of listeners) listener();
}

// ── Module side-effect: attach listener before React renders ────────────────

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredEvent = e as BeforeInstallPromptEvent;
    notifyListeners();
  });

  window.addEventListener("appinstalled", () => {
    deferredEvent = null;
    notifyListeners();
  });
}

// ── useSyncExternalStore interface ──────────────────────────────────────────

export function subscribe(onStoreChange: () => void): () => void {
  listeners.push(onStoreChange);
  return () => {
    listeners = listeners.filter((l) => l !== onStoreChange);
  };
}

/**
 * Availability as a PRIMITIVE, deliberately, not as a snapshot object.
 *
 * `useSyncExternalStore` compares snapshots by reference, so returning a fresh object literal from
 * here is an infinite render loop, and hand-caching one object invites the cache and the real state
 * to drift apart. A boolean is compared by value, so the stability problem cannot exist — and
 * whether a prompt is held is the only thing any caller needs from this store. The event itself is
 * read from module state by {@link promptInstall}, never handed out.
 */
export function getSnapshot(): boolean {
  return deferredEvent !== null;
}

export function getServerSnapshot(): boolean {
  return false;
}

// ── Install action ──────────────────────────────────────────────────────────

export type PromptResult =
  | { outcome: "accepted" }
  | { outcome: "dismissed" }
  | { outcome: "unavailable" };

/**
 * Replays the deferred prompt. A deferred `beforeinstallprompt` can only be used ONCE — the
 * browser discards it after `prompt()` is called regardless of whether the user accepts or
 * dismisses. We clear our reference to match.
 */
export async function promptInstall(): Promise<PromptResult> {
  if (!deferredEvent) return { outcome: "unavailable" };

  const event = deferredEvent;
  deferredEvent = null;
  notifyListeners();

  try {
    await event.prompt();
    const { outcome } = await event.userChoice;
    return { outcome };
  } catch {
    return { outcome: "unavailable" };
  }
}

// ── Standalone / iOS detection ──────────────────────────────────────────────

export function isRunningStandalone(): boolean {
  if (typeof window === "undefined") return false;

  // matchMedia is absent in jsdom and in some embedded webviews. Checked separately from `window`
  // because the two fail independently, and an unguarded call here would throw during render.
  if (typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches) {
    return true;
  }

  // iOS Safari does not implement the display-mode media query. The proprietary
  // `navigator.standalone` is the only signal for an installed iOS web app.
  if ((navigator as unknown as { standalone?: boolean }).standalone === true) return true;

  return false;
}

export function isIosSafari(): boolean {
  if (typeof window === "undefined") return false;

  const ua = navigator.userAgent;

  // (a) iPadOS 13+ reports a desktop Macintosh user agent. A real Mac has at most one touch
  //     point; iPadOS exposes many. This is the only reliable iPadOS detection post-13.
  const isIosDevice =
    /iPhone|iPad|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);

  if (!isIosDevice) return false;

  // (b) Chrome, Firefox, and Edge on iOS are Safari WebKit underneath but CANNOT install to the
  //     home screen — only Safari proper can. Offering "Add to Home Screen" instructions in those
  //     browsers tells the user to do something impossible.
  if (/CriOS|FxiOS|EdgiOS/.test(ua)) return false;

  return true;
}

// ── Test reset ──────────────────────────────────────────────────────────────

export function __resetInstallPromptForTests(): void {
  deferredEvent = null;
  listeners = [];
}
